/**
 * Memory-only authority for work that can expose or mutate private data.
 *
 * The persisted auth store is deliberately only UI chrome.  A caller has
 * mutation authority only after a fresh, no-store GET /api/auth/me returned
 * the opaque server-issued principal fence in this JavaScript lifetime.
 */
import type { MeResponse } from '@chaos/types/auth';
import { useSyncExternalStore } from 'react';

import { useAuthStore } from '../stores/auth-store';
import { ApiError, apiJson } from './api/client';

export type VerifiedPrincipalStatus =
  | 'unverified'
  | 'verifying'
  | 'verified'
  | 'unauthenticated';

export interface VerifiedPrincipalSnapshot {
  readonly userId: string | null;
  readonly principalFence: string | null;
  readonly verifiedAt: number | null;
  /** Changes only when the server principal/session changes, not on a same-session refresh. */
  readonly generation: number;
  readonly status: VerifiedPrincipalStatus;
}

export type VerifiedPrincipalRef = Readonly<VerifiedPrincipalSnapshot>;

type VerifiedMeResponse = MeResponse & { principalFence: string };
type VerificationResult = { me: MeResponse; snapshot: VerifiedPrincipalSnapshot };
type AuthTransitionKind = 'login' | 'signup' | 'logout';
type AuthCoherenceMessage =
  | {
      type: 'auth-transition';
      phase: 'start' | 'complete';
      kind: AuthTransitionKind;
      transitionId: string;
      source: string;
    }
  | { type: 'principal-changed'; source: string };
type OutboundAuthCoherenceMessage =
  | Omit<Extract<AuthCoherenceMessage, { type: 'auth-transition' }>, 'source'>
  | Omit<Extract<AuthCoherenceMessage, { type: 'principal-changed' }>, 'source'>;

const CHANNEL_NAME = 'chaos-auth-coherence-v1';
const TAB_SOURCE =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random()}`;

const EMPTY_SNAPSHOT: VerifiedPrincipalSnapshot = Object.freeze({
  userId: null,
  principalFence: null,
  verifiedAt: null,
  generation: 0,
  status: 'unverified',
});

let snapshot = EMPTY_SNAPSHOT;
let lastVerifiedFingerprint: string | null = null;
let requestEpoch = 0;
let inFlight: { epoch: number; promise: Promise<VerificationResult> } | null = null;
let resetPrincipalCaches: () => void = () => {};
let stopCoherence: (() => void) | null = null;
let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
let recoveryStep = 0;
let outboundChannel: BroadcastChannel | null = null;
const listeners = new Set<() => void>();
const activeAuthTransitions = new Set<string>();

export class StalePrincipalVerificationError extends Error {
  constructor() {
    super('A newer principal verification superseded this response');
    this.name = 'StalePrincipalVerificationError';
  }
}

export class MissingPrincipalFenceError extends Error {
  constructor() {
    super('GET /api/auth/me did not return a principal fence');
    this.name = 'MissingPrincipalFenceError';
  }
}

export class AuthTransitionInProgressError extends Error {
  constructor() {
    super('Principal verification is paused during an auth transition');
    this.name = 'AuthTransitionInProgressError';
  }
}

/**
 * How long one GET /api/auth/me may take before the tab stops waiting for it.
 *
 * Without a bound, a /me that never answers kept the shared single-flight
 * request alive for ever: the tab stayed `verifying` (private tree veiled),
 * every later caller - the focus / visibility handlers included - JOINED that
 * same hung request, and nothing could recover short of a reload. The answer
 * normally takes milliseconds; ten seconds is far past any healthy gateway and
 * still inside the chat queue's 15 s attempt deadline, so a drain attempt's
 * principal check ends as "sign-in could not be checked" (retried, with its
 * notice) instead of "took too long".
 */
export const ME_REQUEST_TIMEOUT_MS = 10_000;

/**
 * After a ROUTINE check (the tab was verified a moment ago) fails for a reason
 * that is not a verdict - the network, a timeout, a 5xx/429 - the tab retries
 * by itself on this ladder while it is visible. A failed check revokes all
 * private authority and the private tree stays behind the sign-in splash, so
 * "wait for the next focus event" left an unattended tab on a spinner. After
 * the last step it stops; focus / visibility / pageshow still re-check.
 */
export const RECOVERY_RETRY_DELAYS_MS: readonly number[] = Object.freeze([1_000, 3_000, 10_000, 30_000]);

export class MeRequestTimeoutError extends Error {
  constructor() {
    super('GET /api/auth/me did not answer in time');
    this.name = 'MeRequestTimeoutError';
  }
}

function emit(next: VerifiedPrincipalSnapshot): void {
  snapshot = Object.freeze(next);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getVerifiedPrincipalSnapshot(): VerifiedPrincipalSnapshot {
  return snapshot;
}

/** A reactive, useSyncExternalStore-safe view of the memory-only authority. */
export function useVerifiedPrincipal(): VerifiedPrincipalSnapshot {
  return useSyncExternalStore(
    subscribe,
    getVerifiedPrincipalSnapshot,
    getVerifiedPrincipalSnapshot,
  );
}

/** Capture an immutable value for comparison after a delayed Web Lock wait. */
export function captureVerifiedPrincipalRef(): VerifiedPrincipalRef {
  return snapshot;
}

export function sameVerifiedPrincipal(
  expected: Pick<VerifiedPrincipalRef, 'userId' | 'principalFence'>,
  actual: VerifiedPrincipalRef = snapshot,
): boolean {
  return (
    actual.status === 'verified' &&
    expected.userId !== null &&
    expected.principalFence !== null &&
    actual.userId === expected.userId &&
    actual.principalFence === expected.principalFence
  );
}

/** The gateway's only two principal-coherence refusals for unsafe cookie calls. */
export function isPrincipalFenceRefusal(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as { status?: unknown; code?: unknown };
  return (candidate.status === 428 && candidate.code === 'expected_principal_required') ||
    (candidate.status === 409 && candidate.code === 'principal_changed');
}

/**
 * Revoke the stale in-memory authority synchronously, then attempt a no-store
 * bootstrap. Callers must still fail the current action closed; this helper
 * never retries or replays an unsafe request.
 */
export function recoverFromPrincipalFenceRefusal(reason: string): void {
  void reason;
  invalidateVerifiedPrincipal('unverified');
  void getFreshVerifiedPrincipal().catch(() => undefined);
}

/** AuthBootstrap wires the singleton QueryClient here without creating an import cycle. */
export function setVerifiedPrincipalCacheResetter(resetter: () => void): () => void {
  resetPrincipalCaches = resetter;
  return () => {
    if (resetPrincipalCaches === resetter) resetPrincipalCaches = () => {};
  };
}

/**
 * Synchronously revoke all private-data authority. An unresolved/failed /me
 * must never leave the previous fence usable.
 */
export function invalidateVerifiedPrincipal(
  status: Exclude<VerifiedPrincipalStatus, 'verified'> = 'unverified',
  { clearCaches = true }: { clearCaches?: boolean } = {},
): void {
  requestEpoch += 1;
  inFlight = null;
  cancelRecovery();
  if (status === 'unauthenticated') recoveryStep = 0;
  emit({
    userId: null,
    principalFence: null,
    verifiedAt: null,
    generation: snapshot.generation,
    status,
  });
  if (clearCaches) resetPrincipalCaches();
}

function cancelRecovery(): void {
  if (recoveryTimer !== null) clearTimeout(recoveryTimer);
  recoveryTimer = null;
}

/**
 * Re-check on the RECOVERY_RETRY_DELAYS_MS ladder after a routine check failed
 * without a verdict. One timer at a time; it does nothing if the tab has since
 * been verified, signed out, is hidden (the visibility handler re-checks on
 * return) or is inside an auth transition.
 */
function scheduleRecovery(): void {
  if (recoveryTimer !== null) return;
  const delay = RECOVERY_RETRY_DELAYS_MS[recoveryStep];
  if (delay === undefined) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  recoveryStep += 1;
  recoveryTimer = setTimeout(() => {
    recoveryTimer = null;
    if (snapshot.status !== 'unverified' || inFlight !== null || activeAuthTransitions.size > 0) return;
    // A failure schedules the next step from startFreshVerification's catch.
    void startFreshVerification().catch(() => undefined);
  }, delay);
}

/**
 * Revoke action authority for a routine freshness check without destroying
 * the mounted owner component that initiated it. The retained identity is
 * display-only while status is `verifying`: `sameVerifiedPrincipal` and every
 * private request gate still reject it. A failure or an actual transition uses
 * `invalidateVerifiedPrincipal` and clears it completely.
 */
function beginRoutineVerification(): void {
  requestEpoch += 1;
  inFlight = null;
  const retainMountedIdentity =
    snapshot.status === 'verified'
    && snapshot.userId !== null
    && snapshot.principalFence !== null;
  emit({
    userId: retainMountedIdentity ? snapshot.userId : null,
    principalFence: retainMountedIdentity ? snapshot.principalFence : null,
    verifiedAt: retainMountedIdentity ? snapshot.verifiedAt : null,
    generation: snapshot.generation,
    status: 'verifying',
  });
}

function broadcast(message: OutboundAuthCoherenceMessage): void {
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      // Reuse one sender so start/principal-changed/complete retain FIFO order.
      // Separate ephemeral channel objects do not provide that ordering guarantee.
      outboundChannel ??= new BroadcastChannel(CHANNEL_NAME);
      outboundChannel.postMessage({ ...message, source: TAB_SOURCE });
    } catch {
      outboundChannel?.close();
      outboundChannel = null;
      // Browser privacy modes can expose the constructor but refuse storage-
      // backed channels. Local fail-closed invalidation has already happened.
    }
  }
}

/**
 * Begin a two-phase cookie/session transition. Every tab revokes authority at
 * start and peers wait for completion before they attempt a fresh /me, so a
 * logout cannot briefly restore the old fence before server revocation lands.
 */
export function beginAuthTransition(kind: AuthTransitionKind): {
  getFreshVerifiedMe: () => Promise<MeResponse>;
  complete: () => void;
} {
  const transitionId = `${TAB_SOURCE}:${Date.now()}:${Math.random()}`;
  activeAuthTransitions.add(transitionId);
  invalidateVerifiedPrincipal(kind === 'logout' ? 'unauthenticated' : 'unverified');
  if (kind === 'logout') useAuthStore.getState().setUnauthenticated();
  broadcast({ type: 'auth-transition', phase: 'start', kind, transitionId });

  let completed = false;
  return {
    getFreshVerifiedMe: async () =>
      (await startFreshVerification({ allowedAuthTransitionId: transitionId })).me,
    complete: () => {
      if (completed) return;
      completed = true;
      activeAuthTransitions.delete(transitionId);
      broadcast({ type: 'auth-transition', phase: 'complete', kind, transitionId });
    },
  };
}

async function fetchFreshMe(): Promise<VerifiedMeResponse> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Raced rather than only aborted: a fetch that ignores its signal (a stuck
  // service worker, a test double) must not be able to outlive the bound.
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new MeRequestTimeoutError());
    }, ME_REQUEST_TIMEOUT_MS);
  });
  const request = apiJson<VerifiedMeResponse>('/auth/me', {
    method: 'GET',
    cache: 'no-store',
    signal: controller.signal,
    headers: {
      'Cache-Control': 'no-cache, no-store, max-age=0',
      Pragma: 'no-cache',
    },
  });
  // The loser of the race must not surface as an unhandled rejection.
  request.catch(() => undefined);
  try {
    return await Promise.race([request, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

function startFreshVerification(
  { allowedAuthTransitionId }: { allowedAuthTransitionId?: string } = {},
): Promise<VerificationResult> {
  if (
    activeAuthTransitions.size > 0 &&
    !(
      allowedAuthTransitionId !== undefined &&
      activeAuthTransitions.size === 1 &&
      activeAuthTransitions.has(allowedAuthTransitionId)
    )
  ) {
    return Promise.reject(new AuthTransitionInProgressError());
  }
  // Same-generation callers join one network request. This check is before
  // invalidation on purpose: focus + visibility + pageshow often arrive as a burst.
  if (inFlight) return inFlight.promise;

  // The authority itself must flap fail-closed for every fresh check. Query
  // data is cleared only if the check fails or discovers a changed principal;
  // clearing it for a successful same-principal mutation preflight would blank
  // the whole application on every Send/Retry.
  // A failed check that follows a verified (or already recovering) tab is worth
  // retrying by itself; a failed first bootstrap has its own retry loop in
  // AuthBootstrap and must not get a second one here.
  const routineCheck = snapshot.status === 'verified' || recoveryStep > 0;
  beginRoutineVerification();
  const epoch = requestEpoch;
  const promise = (async (): Promise<VerificationResult> => {
    try {
      const raw = await fetchFreshMe();
      if (epoch !== requestEpoch) throw new StalePrincipalVerificationError();
      if (typeof raw.principalFence !== 'string' || raw.principalFence.length === 0) {
        throw new MissingPrincipalFenceError();
      }

      const { principalFence, ...me } = raw;
      const fingerprint = `${me.user.id}\u0000${principalFence}`;
      const principalChanged = fingerprint !== lastVerifiedFingerprint;
      const generation = snapshot.generation + (principalChanged ? 1 : 0);
      const next: VerifiedPrincipalSnapshot = Object.freeze({
        userId: me.user.id,
        principalFence,
        verifiedAt: Date.now(),
        generation,
        status: 'verified',
      });

      // Remove the previous principal's cached payload before the new identity
      // can become observable. Same-principal refreshes retain warm queries.
      if (principalChanged) resetPrincipalCaches();

      // Membership-valid selection is part of accepting the new principal.
      useAuthStore.getState().setSession(me);
      lastVerifiedFingerprint = fingerprint;
      cancelRecovery();
      recoveryStep = 0;
      emit(next);
      // The current tab already holds `next`; only peers must revalidate.
      if (principalChanged) broadcast({ type: 'principal-changed' });
      return { me, snapshot: next };
    } catch (error) {
      if (epoch === requestEpoch) {
        const unauthenticated =
          error instanceof ApiError && (error.status === 401 || error.status === 403);
        invalidateVerifiedPrincipal(unauthenticated ? 'unauthenticated' : 'unverified');
        if (unauthenticated) {
          useAuthStore.getState().setUnauthenticated();
        } else if (routineCheck && !(error instanceof MissingPrincipalFenceError)) {
          scheduleRecovery();
        }
      }
      throw error;
    } finally {
      // The promise is created before `inFlight` is assigned, so TypeScript's
      // initializer control-flow cannot see the later assignment here.
      const activeRequest = inFlight as {
        epoch: number;
        promise: Promise<VerificationResult>;
      } | null;
      if (activeRequest?.epoch === epoch) inFlight = null;
    }
  })();
  inFlight = { epoch, promise };
  return promise;
}

/**
 * A SAFE read that meets a routine freshness check on the SAME principal may
 * wait for it instead of being refused.
 *
 * Returning to a tab fires `visibilitychange`; the coherence listener starts a
 * routine /me check (status `verifying`) and, in the very same event, TanStack's
 * focus manager refetches every stale query. Refusing those reads with
 * `principal_unverified` made them fail for a window that closes by itself a
 * moment later (staging 2026-10-10: the card "Workspace configuration" block
 * turned red on every return after 30 s away and never recovered).
 *
 * This resolves to the fresh snapshot ONLY when the check succeeds and the
 * principal is the one the caller already had (same user, same generation - a
 * rotated fence or another user bumps the generation). In every other case
 * (no retained identity, a failed check, a changed principal, a transition in
 * progress) it resolves to null and the caller fails closed exactly as before.
 * It never extends authority: the read is sent only after a verified fence is
 * in memory, with that fence attached. Unsafe requests do not use it.
 *
 * The wait is as long as the check itself and no longer: /me is bounded by
 * ME_REQUEST_TIMEOUT_MS, and a timed-out check resolves this to null (the read
 * is then refused `principal_unverified`, as for any other failed check).
 */
export async function awaitSamePrincipalVerification(): Promise<VerifiedPrincipalSnapshot | null> {
  const before = snapshot;
  if (
    before.status !== 'verifying'
    || before.userId === null
    || before.principalFence === null
    || inFlight === null
  ) {
    return null;
  }
  try {
    await inFlight.promise;
  } catch {
    return null;
  }
  const after = snapshot;
  return after.status === 'verified'
    && after.userId === before.userId
    && after.principalFence !== null
    && after.generation === before.generation
    ? after
    : null;
}

/** Always performs (or joins) a fresh no-store /me verification. Rejects fail-closed. */
export async function getFreshVerifiedPrincipal(): Promise<VerifiedPrincipalSnapshot> {
  return (await startFreshVerification()).snapshot;
}

/** Auth API adapter: same singleflight verification, returning the ordinary /me payload. */
export async function getFreshVerifiedMe(): Promise<MeResponse> {
  return (await startFreshVerification()).me;
}

/**
 * Install cross-tab and browser-lifecycle revalidation. Idempotent per tab.
 * The returned disposer is primarily for AuthBootstrap/tests.
 */
export function startVerifiedPrincipalCoherence(): () => void {
  if (stopCoherence) return stopCoherence;
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};

  const revalidate = () => {
    if (activeAuthTransitions.size > 0) return;
    void getFreshVerifiedPrincipal().catch(() => {});
  };
  const onFocus = () => revalidate();
  const onPageShow = () => revalidate();
  const onPageHide = (event: PageTransitionEvent) => {
    // A BFCache document keeps its entire DOM and JavaScript heap. Revoke the
    // current fence before the snapshot is frozen so restoring it cannot expose
    // principal A while the shared cookie jar has moved to principal B.
    if (event.persisted) invalidateVerifiedPrincipal('unverified');
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') revalidate();
  };
  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent<AuthCoherenceMessage>) => {
        const message = event.data;
        if (!message || message.source === TAB_SOURCE) return;
        if (message.type === 'auth-transition') {
          if (message.phase === 'start') {
            activeAuthTransitions.add(message.transitionId);
            invalidateVerifiedPrincipal(
              message.kind === 'logout' ? 'unauthenticated' : 'unverified',
            );
            if (message.kind === 'logout') useAuthStore.getState().setUnauthenticated();
            return;
          }
          activeAuthTransitions.delete(message.transitionId);
          revalidate();
          return;
        }
        // A changed remote session supersedes any older in-flight response, but
        // revalidation remains paused until its auth transition completes.
        invalidateVerifiedPrincipal('verifying');
        revalidate();
      };
    } catch {
      channel = null;
    }
  }

  window.addEventListener('focus', onFocus);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onVisible);

  stopCoherence = () => {
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('visibilitychange', onVisible);
    channel?.close();
    stopCoherence = null;
  };
  return stopCoherence;
}

/** Narrow reset surface for isolated unit tests. Never used by product code. */
export function resetVerifiedPrincipalForTests(): void {
  stopCoherence?.();
  stopCoherence = null;
  requestEpoch += 1;
  inFlight = null;
  cancelRecovery();
  recoveryStep = 0;
  snapshot = EMPTY_SNAPSHOT;
  lastVerifiedFingerprint = null;
  resetPrincipalCaches = () => {};
  activeAuthTransitions.clear();
  outboundChannel?.close();
  outboundChannel = null;
  listeners.clear();
}
